import React, { useEffect, useState } from 'react';
import { Tag, Save, FileDown, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PRESETS = [
  { value: '50x25', label: '50 × 25 mm (thermal roll)' },
  { value: '38x25', label: '38 × 25 mm (thermal roll)' },
  { value: '100x50', label: '100 × 50 mm (thermal roll)' },
  { value: 'a4_65', label: 'A4 sheet — 65 labels (38.1 × 21.2 mm)' },
  { value: 'custom', label: 'Custom size…' },
];

export default function LabelSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/labels/settings`, auth())
      .then(res => setS(res.data))
      .catch(() => toast.error('Failed to load label settings'));
  }, []);

  const set = (k, v) => setS(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/labels/settings`, s, auth());
      setS(res.data);
      toast.success('Label settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      await axios.put(`${API}/labels/settings`, s, auth());
      const res = await axios.post(`${API}/labels/generate`, { items: [], sample: true }, { ...auth(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to generate sample');
    } finally {
      setPreviewing(false);
    }
  };

  if (!s) return <div className="p-6 text-wt-text-muted">Loading…</div>;

  return (
    <div className="p-4 md:p-6 max-w-2xl space-y-6" data-testid="label-settings-page">
      <div className="flex items-center gap-3">
        <Tag className="h-6 w-6 text-wt-gold" />
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary">Label Settings</h1>
          <p className="text-sm text-wt-text-muted">SKU barcode labels for products — printed from Inventory or after receiving a PO</p>
        </div>
      </div>

      <Card className="bg-wt-paper border-wt-border">
        <CardHeader><CardTitle className="text-wt-text-primary text-base">Label size & barcode</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-wt-text-primary">Label size</Label>
              <Select value={s.preset} onValueChange={(v) => set('preset', v)}>
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  {PRESETS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-wt-text-primary">Barcode type</Label>
              <Select value={s.barcode_type} onValueChange={(v) => set('barcode_type', v)}>
                <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-barcode-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-wt-paper border-wt-border">
                  <SelectItem value="code128">Code 128 (recommended for scanners)</SelectItem>
                  <SelectItem value="qr">QR code</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {s.preset === 'custom' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Width (mm)</Label>
                <Input type="number" min="15" max="210" step="0.1" value={s.custom_width_mm}
                  onChange={(e) => set('custom_width_mm', parseFloat(e.target.value) || 50)}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-custom-width-input" />
              </div>
              <div>
                <Label className="text-wt-text-primary">Height (mm)</Label>
                <Input type="number" min="10" max="297" step="0.1" value={s.custom_height_mm}
                  onChange={(e) => set('custom_height_mm', parseFloat(e.target.value) || 25)}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-custom-height-input" />
              </div>
              <p className="col-span-2 text-[11px] text-wt-text-muted -mt-2">Enter your label roll's exact size — one label per page, printed at that dimension</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-wt-paper border-wt-border">
        <CardHeader><CardTitle className="text-wt-text-primary text-base">Label content</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-wt-text-primary">Product name</Label>
            <Switch checked={s.show_name} onCheckedChange={(v) => set('show_name', v)} data-testid="label-show-name-switch" />
          </div>
          {s.show_name && (
            <div>
              <Label className="text-wt-text-primary">Crop product name to <span className="text-wt-gold font-semibold">{s.name_max_chars}</span> characters</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="range" min="5" max="100" value={s.name_max_chars}
                  onChange={(e) => set('name_max_chars', parseInt(e.target.value))}
                  className="flex-1 accent-[var(--wt-gold,#d4af37)]"
                  data-testid="label-name-crop-slider"
                />
                <Input
                  type="number" min="5" max="100" value={s.name_max_chars}
                  onChange={(e) => set('name_max_chars', Math.max(5, Math.min(100, parseInt(e.target.value) || 5)))}
                  className="w-20 bg-wt-bg border-wt-border text-wt-text-primary"
                  data-testid="label-name-crop-input"
                />
              </div>
              <p className="text-[11px] text-wt-text-muted mt-1">Longer names are cut with “…” so they never overflow the label</p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label className="text-wt-text-primary">SKU text under barcode</Label>
            <Switch checked={s.show_sku_text} onCheckedChange={(v) => set('show_sku_text', v)} data-testid="label-show-sku-switch" />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-wt-text-primary">Price</Label>
            <Switch checked={s.show_price} onCheckedChange={(v) => set('show_price', v)} data-testid="label-show-price-switch" />
          </div>
          {s.show_price && (
            <div>
              <Label className="text-wt-text-primary">Currency prefix <span className="text-wt-text-muted font-normal">(e.g. AED, ₹)</span></Label>
              <Input value={s.currency_prefix} onChange={(e) => set('currency_prefix', e.target.value)} placeholder="AED " className="mt-1 w-32 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-currency-input" />
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label className="text-wt-text-primary">Company name on top</Label>
            <Switch checked={s.show_company} onCheckedChange={(v) => set('show_company', v)} data-testid="label-show-company-switch" />
          </div>
          {s.show_company && (
            <Input value={s.company_label} onChange={(e) => set('company_label', e.target.value)} placeholder="Wooden Twist" className="bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-company-input" />
          )}
        </CardContent>
      </Card>

      <Card className="bg-wt-paper border-wt-border">
        <CardHeader><CardTitle className="text-wt-text-primary text-base">Default copies</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-wt-text-primary">Copies per SKU</Label>
            <Select value={s.copies_mode} onValueChange={(v) => set('copies_mode', v)}>
              <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-copies-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="fixed">Fixed number</SelectItem>
                <SelectItem value="stock">Match current stock qty</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {s.copies_mode === 'fixed' && (
            <div>
              <Label className="text-wt-text-primary">Default number</Label>
              <Input type="number" min="1" max="100" value={s.default_copies}
                onChange={(e) => set('default_copies', Math.max(1, parseInt(e.target.value) || 1))}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="label-default-copies-input" />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button onClick={save} disabled={saving} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="label-settings-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Settings
        </Button>
        <Button onClick={preview} disabled={previewing} variant="outline" className="border-wt-border text-wt-text-primary" data-testid="label-sample-btn">
          {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          Preview Sample PDF
        </Button>
      </div>
    </div>
  );
}
